import React, { useState, useEffect } from 'react';
import axios from 'axios';
import { Zap, CreditCard, Star, Building2, User, RefreshCw, CheckCircle, Loader, ShieldCheck, Image, Video, Music, Check, X } from 'lucide-react';
import toast from 'react-hot-toast';
import { useAuth } from '../contexts/AuthContext';
import './BonusTopup.css';

const formatNumber = (num) => {
  if (num === null || num === undefined) return '0';
  return Math.round(num).toString().replace(/\B(?=(\d{3})+(?!\d))/g, ' ');
};

const formatGensValue = (value) => {
  if (!value) return '—';
  const str = String(value);
  if (str.includes('–')) {
    const [a, b] = str.split('–').map((part) => formatNumber(parseInt(part.trim(), 10)));
    return `${a}–${b}`;
  }
  return formatNumber(parseInt(str.replace(/\s/g, ''), 10));
};

const formatModelGenerations = (tierKey, model, access) => {
  if (access === false) return '—';
  if (model.gens?.[tierKey]) return formatGensValue(model.gens[tierKey]);
  return '—';
};

// Доступность и лимиты генераций/мес из photo_tariffs_5.csv и video_tariffs_5.csv
const MODEL_ACCESS = {
  photo: [
    { name: 'Nano Banana', tokens: 1.5, mini: true, start: true, pro: true, gens: { mini: '100', start: '433', pro: '1100' } },
    { name: 'Nano Banana Pro', tokens: '2–5', mini: true, start: true, pro: true, gens: { mini: '30–75', start: '130–325', pro: '330–825' } },
    { name: 'Nano Banana 2', tokens: '2–4', mini: true, start: true, pro: true, gens: { mini: '37–75', start: '162–325', pro: '412–825' } },
    { name: 'Nano Banana 2.1', tokens: '3–6', mini: true, start: true, pro: true, gens: { mini: '25–50', start: '108–216', pro: '275–550' } },
    { name: 'GPT Image 2', tokens: '1–18', mini: true, start: true, pro: true, gens: { mini: '8–150', start: '36–650', pro: '91–1650' } },
    { name: 'Seedream 5 Pro', tokens: '3–7', mini: true, start: true, pro: true, gens: { mini: '21–50', start: '92–216', pro: '235–550' } },
  ],
  video: [
    { name: 'Kling 3.0', tokens: '9–50', mini: false, start: true, pro: true, badge: 'Старт+', gens: { start: '9–43', pro: '24–110' } },
    { name: 'Kling 2.5 Turbo', tokens: 15, mini: true, start: true, pro: true, gens: { mini: '10', start: '43', pro: '110' } },
    { name: 'Kling 2.6', tokens: '12–25', mini: false, start: true, pro: true, badge: 'Старт+', gens: { start: '26–54', pro: '66–137' } },
    { name: 'Seedance 1.5 Pro', tokens: 20, mini: true, start: true, pro: true, gens: { mini: '6', start: '26', pro: '66' } },
    { name: 'Seedance 2', tokens: '20–200', mini: true, start: true, pro: true, gens: { mini: '0–7', start: '3–32', pro: '8–82' } },
    { name: 'Seedance 2.5', tokens: '26.5–240', mini: true, start: true, pro: true, gens: { mini: '0–5', start: '2–24', pro: '6–62' } },
    { name: 'Veo 3.1', tokens: '16–144', mini: false, start: true, pro: true, badge: 'Старт+', gens: { start: '5–43', pro: '11–110' } },
  ],
  audio: [
    { name: 'Suno v6', tokens: 5, mini: true, start: true, pro: true, gens: { mini: '30', start: '130', pro: '330' } },
    { name: 'ElevenLabs v2', tokens: '1–25', mini: true, start: true, pro: true, gens: { mini: '6–150', start: '26–650', pro: '66–1650' } },
    { name: 'ElevenLabs v3', tokens: '1–25', mini: true, start: true, pro: true, gens: { mini: '6–150', start: '26–650', pro: '66–1650' } },
  ],
};

// Описания тарифов (из Higgsfield)
const TARIFF_DESCRIPTIONS = {
  'Мини': 'Для первых экспериментов с AI',
  'Старт': 'Для регулярной работы с AI',
  'Про': 'Для профессиональных проектов',
};

const TARIFF_ORIGINAL_PRICES = {
  'Старт': 2113,
  'Про': 5363,
};

function BonusTopup({ currentBalance, onBalanceUpdate }) {
  const { user } = useAuth();
  const [tariffs, setTariffs] = useState([]);
  const [topupTariffs, setTopupTariffs] = useState([]);
  const [subscriptionInfo, setSubscriptionInfo] = useState(null);
  const [loading, setLoading] = useState(true);
  const [isProcessing, setIsProcessing] = useState(false);
  const [processingTariffId, setProcessingTariffId] = useState(null);
  const [activeTab, setActiveTab] = useState('individuals');

  const [consentChecked, setConsentChecked] = useState({});
  const [showCheckboxError, setShowCheckboxError] = useState({});
  const handleConsentChange = (tariffId, checked) => {
    setConsentChecked(prev => ({ ...prev, [tariffId]: checked }));
    if (checked) {
      setShowCheckboxError(prev => ({ ...prev, [tariffId]: false }));
    }
  };

  const [contactData, setContactData] = useState({
    companyName: '', contactName: '', email: '', phone: '', message: ''
  });
  const [contactSubmitting, setContactSubmitting] = useState(false);
  const [contactSuccess, setContactSuccess] = useState(false);

  const processingTimeoutRef = React.useRef(null);
  const checkIntervalRef = React.useRef(null);
  const isProcessingRef = React.useRef(false);

  useEffect(() => {
    return () => {
      if (checkIntervalRef.current) clearInterval(checkIntervalRef.current);
      if (processingTimeoutRef.current) clearTimeout(processingTimeoutRef.current);
    };
  }, []);

  useEffect(() => {
    fetchTariffs();
    fetchSubscriptionInfo();

    const pendingOrderId = sessionStorage.getItem('pending_payment_order_id');
    if (pendingOrderId) {
      axios.get(`/api/payments/check-status/${pendingOrderId}`)
        .then(res => {
          if (res.data.status === 'completed' || res.data.status === 'failed' || res.data.status === 'expired') {
            sessionStorage.removeItem('pending_payment_order_id');
            if (res.data.status === 'completed') {
              if (onBalanceUpdate) onBalanceUpdate();
              fetchSubscriptionInfo();
            }
          }
        }).catch(() => { });
    }
  }, []);

  const fetchSubscriptionInfo = async () => {
    try {
      const response = await axios.get('/api/payments/my-subscription');
      if (response.data.has_subscription) {
        setSubscriptionInfo(response.data.subscription);
      } else {
        setSubscriptionInfo(null);
      }
    } catch (error) {
      console.error('Error fetching subscription:', error);
    }
  };

  const fetchTariffs = async () => {
    try {
      const [subResponse, topupResponse] = await Promise.all([
        axios.get('/api/payments/subscription-tariffs'),
        axios.get('/api/payments/tariffs?tariff_type=token_topup'),
      ]);
      setTariffs(subResponse.data);
      setTopupTariffs(topupResponse.data);
    } catch (error) {
      console.error('Error fetching tariffs:', error);
      toast.error('Не удалось загрузить списки подписок');
    } finally {
      setLoading(false);
    }
  };

  const checkPendingPayment = React.useCallback(async (orderId) => {
    checkIntervalRef.current = setInterval(async () => {
      try {
        const statusResponse = await axios.get(`/api/payments/check-status/${orderId}`);
        if (statusResponse.data.status === 'completed') {
          if (checkIntervalRef.current) { clearInterval(checkIntervalRef.current); checkIntervalRef.current = null; }
          toast.success(`Успешно! Оплата прошла.`);
          if (onBalanceUpdate) onBalanceUpdate();
          fetchSubscriptionInfo();
          setIsProcessing(false); isProcessingRef.current = false; setProcessingTariffId(null);
          sessionStorage.removeItem('pending_payment_order_id');
        } else if (statusResponse.data.status === 'failed' || statusResponse.data.status === 'cancelled') {
          if (checkIntervalRef.current) { clearInterval(checkIntervalRef.current); checkIntervalRef.current = null; }
          toast.error('Оплата не прошла');
          setIsProcessing(false); isProcessingRef.current = false; setProcessingTariffId(null);
          sessionStorage.removeItem('pending_payment_order_id');
        }
      } catch (error) { console.error('Error checking payment status:', error); }
    }, 3000);

    processingTimeoutRef.current = setTimeout(() => {
      if (checkIntervalRef.current) clearInterval(checkIntervalRef.current);
      sessionStorage.removeItem('pending_payment_order_id');
      if (isProcessingRef.current) {
        setIsProcessing(false); isProcessingRef.current = false; setProcessingTariffId(null);
      }
    }, 180000);
  }, [onBalanceUpdate]);

  const handleSubscribeBtn = (tariff) => {
    if (tariff.tariff_type === 'subscription' && !consentChecked[tariff.id]) {
      setShowCheckboxError(prev => ({ ...prev, [tariff.id]: true }));
      toast.error('Пожалуйста, поставьте галочку согласия');
      return;
    }
    handleSubscribe(tariff);
  };

  const handleSubscribe = async (tariff) => {
    if (isProcessing && processingTariffId === tariff.id) return;

    if (tariff.tariff_type === 'token_topup' && !subscriptionInfo) {
      toast.error('Докупка токенов доступна только при активной подписке');
      return;
    }

    setIsProcessing(true); isProcessingRef.current = true; setProcessingTariffId(tariff.id);

    try {
      const endpoint = tariff.tariff_type === 'subscription' ? '/api/payments/subscribe' : '/api/payments/create';
      const response = await axios.post(endpoint, { tariff_id: tariff.id });
      const paymentResponse = response.data;

      if (paymentResponse.status === 'completed') {
        const msg = tariff.tariff_type === 'subscription'
          ? 'Подписка оформлена'
          : `Начислено ${formatNumber(tariff.bonuses_amount)} токенов`;
        toast.success(msg);
        if (onBalanceUpdate) onBalanceUpdate();
        fetchSubscriptionInfo();
        setIsProcessing(false); isProcessingRef.current = false; setProcessingTariffId(null);
      } else if (paymentResponse.payment_url) {
        sessionStorage.setItem('pending_payment_order_id', paymentResponse.order_id);
        checkPendingPayment(paymentResponse.order_id);
        window.location.href = paymentResponse.payment_url;
      } else {
        toast.error('Не удалось получить ссылку на оплату');
        setIsProcessing(false); isProcessingRef.current = false; setProcessingTariffId(null);
      }
    } catch (error) {
      console.error('Error creating payment:', error);
      const detail = error.response?.data?.detail;
      toast.error(typeof detail === 'string' ? detail : 'Произошла ошибка при оформлении');
      setIsProcessing(false); isProcessingRef.current = false; setProcessingTariffId(null);
    }
  };

  const handleContactSubmit = async (e) => {
    e.preventDefault();
    setContactSubmitting(true);
    try {
      await axios.post('/api/payments/contact-request', {
        company_name: contactData.companyName, contact_name: contactData.contactName,
        email: contactData.email, phone: contactData.phone || null, message: contactData.message || null
      });
      setContactSuccess(true);
      setContactData({ companyName: '', contactName: '', email: '', phone: '', message: '' });
      toast.success('Заявка успешно отправлена!');
    } catch (error) {
      toast.error('Не удалось отправить заявку. Попробуйте позже.');
    } finally { setContactSubmitting(false); }
  };

  const handleContactChange = (e) => setContactData(prev => ({ ...prev, [e.target.name]: e.target.value }));

  // Маппинг тарифа к ключу доступа
  const getAccessKey = (tariffName) => {
    if (tariffName === 'Мини') return 'mini';
    if (tariffName === 'Старт') return 'start';
    if (tariffName === 'Про') return 'pro';
    return 'mini';
  };

  const renderAccessIcon = (access) => {
    if (access === true) return <span className="access-icon available"><Check size={14} /></span>;
    return <span className="access-icon unavailable"><X size={14} /></span>;
  };

  const renderModelList = (tariffName) => {
    const key = getAccessKey(tariffName);

    return (
      <div className="tariff-models-section">
        <div className="models-section-title">Модели в тарифе</div>
        <div className="models-list">
          <div className="models-category-label"><Image size={14} /> Фото</div>
            {MODEL_ACCESS.photo.map((model, i) => (
              <div key={`p-${i}`} className={`model-access-row ${!model[key] ? 'disabled' : ''}`}>
                <span className="model-access-name">{model.name}</span>
                <span className="model-access-gens">
                  {formatModelGenerations(key, model, model[key])} ген.
                </span>
                {renderAccessIcon(model[key])}
              </div>
            ))}
            <div className="models-category-label"><Video size={14} /> Видео</div>
            {MODEL_ACCESS.video.map((model, i) => (
              <div key={`v-${i}`} className={`model-access-row ${!model[key] ? 'disabled' : ''}`}>
                <span className="model-access-name">
                  {model.name}
                  {model.badge && model[key] && <span className="model-badge">{model.badge}</span>}
                </span>
                <span className="model-access-gens">
                  {formatModelGenerations(key, model, model[key])} ген.
                </span>
                {renderAccessIcon(model[key])}
              </div>
            ))}
            <div className="models-category-label"><Music size={14} /> Аудио</div>
            {MODEL_ACCESS.audio.map((model, i) => (
              <div key={`a-${i}`} className={`model-access-row ${!model[key] ? 'disabled' : ''}`}>
                <span className="model-access-name">{model.name}</span>
                <span className="model-access-gens">
                  {formatModelGenerations(key, model, model[key])} ген.
                </span>
                {renderAccessIcon(model[key])}
              </div>
            ))}
        </div>
      </div>
    );
  };

  return (
    <div className="bonus-topup-container">
      <div className="bonus-header" style={{ justifyContent: 'center' }}>
        <h1>Оформление подписки</h1>
      </div>

      {subscriptionInfo && (
        <div className="active-subscription-card">
          <div className="subscription-header">
            <span className="sub-title">Активная подписка: {subscriptionInfo.tariff_name}</span>
            <span className="sub-status active">Активна</span>
          </div>
          <div className="subscription-details">
            <div className="sub-detail-item">
              <span className="detail-label">Осталось дней</span>
              <span className="detail-value highlight">{subscriptionInfo.days_remaining}</span>
            </div>
            <div className="sub-detail-item">
              <span className="detail-label">Токенов в месяц</span>
              <span className="detail-value highlight">{formatNumber(subscriptionInfo.tariff_bonuses)}</span>
            </div>
          </div>
          {currentBalance != null && (
            <div className="sub-detail-item" style={{ marginTop: '0.5rem' }}>
              <span className="detail-label">Текущий остаток</span>
              <span className="detail-value highlight">
                <Zap size={16} fill="currentColor" style={{ color: '#a0dc70', verticalAlign: 'middle' }} />
                {' '}{formatNumber(currentBalance)} токенов
              </span>
            </div>
          )}
        </div>
      )}

      <div className="tab-switcher-wrapper">
        <div className="tab-switcher">
          <button className={`tab-btn ${activeTab === 'individuals' ? 'active' : ''}`} onClick={() => setActiveTab('individuals')}>
            <User size={16} /> Для себя
          </button>
          <button className={`tab-btn ${activeTab === 'legal' ? 'active' : ''}`} onClick={() => setActiveTab('legal')}>
            <Building2 size={16} /> Для бизнеса
          </button>
        </div>
      </div>

      <div className="topup-layout centralized">
        <div className="topup-main-column">
          {loading ? (
            <div style={{ height: '300px', display: 'flex', alignItems: 'center', justifyContent: 'center' }}>
              <Loader className="spinning text-primary" size={32} />
            </div>
          ) : activeTab === 'individuals' ? (
            <>
              <div className="tariffs-grid">
                {tariffs.map((tariff) => {
                  const desc = TARIFF_DESCRIPTIONS[tariff.name] || tariff.description;
                  const originalPrice = TARIFF_ORIGINAL_PRICES[tariff.name];

                  return (
                    <div key={tariff.id} className={`tariff-card ${tariff.is_popular ? 'popular' : ''}`}>
                      {tariff.is_popular && <div className="popular-badge">Популярный</div>}

                      <div className="tariff-header">
                        <h3 className="tariff-name">{tariff.name}</h3>
                        <p className="tariff-description">{desc}</p>
                        {originalPrice && (
                          <div className="tariff-original-price">
                            ₽{formatNumber(originalPrice)}
                          </div>
                        )}
                        <div className="tariff-price">
                          <span>{formatNumber(tariff.price_rubles)}</span>
                          <span className="currency">₽</span>
                          <span className="period">/ мес</span>
                        </div>
                        {tariff.discount_percent > 0 && (
                          <div className="tariff-discount">Скидка {tariff.discount_percent}%</div>
                        )}
                      </div>

                      <div className="tariff-bonuses">
                        <div className="bonuses-amount">
                          <Zap size={22} fill="currentColor" />
                          <span>{formatNumber(tariff.bonuses_amount)}</span>
                        </div>
                        <div className="bonuses-label">токенов / мес</div>
                      </div>

                      <div className="compliance-wrapper">
                        <label className="consent-checkbox" style={{ border: showCheckboxError[tariff.id] ? '1px solid #e74c3c' : undefined, transition: 'border-color 0.3s' }}>
                          <input type="checkbox" id={`consent-${tariff.id}`} checked={!!consentChecked[tariff.id]} onChange={(e) => handleConsentChange(tariff.id, e.target.checked)} />
                          <span className="consent-text" style={{ color: showCheckboxError[tariff.id] ? '#e74c3c' : undefined }}>
                            Согласие с <a href="/user_agreement.pdf" target="_blank" rel="noopener noreferrer" style={{ color: '#3081ed' }}>правилами</a> на автопродление и списание <strong>{formatNumber(tariff.price_rubles)}&nbsp;₽ / месяц</strong>.
                          </span>
                        </label>
                        {showCheckboxError[tariff.id] && (
                          <div style={{ color: '#e74c3c', fontSize: '0.8rem', marginTop: '-1rem', marginBottom: '1rem', paddingLeft: '1rem', fontWeight: 500 }}>
                            Нужно ваше согласие
                          </div>
                        )}
                      </div>

                      <button className="subscribe-btn" onClick={() => handleSubscribeBtn(tariff)}>
                        {isProcessing && processingTariffId === tariff.id ? (
                          <><RefreshCw size={18} className="spinning" /> Оплата...</>
                        ) : (
                          <><CreditCard size={18} /> Купить подписку</>
                        )}
                      </button>

                      {renderModelList(tariff.name)}
                    </div>
                  );
                })}
              </div>

              {topupTariffs.length > 0 && (
                <div className="one-time-packages-section">
                  <h2 className="one-time-header">Докупка токенов</h2>
                  <p className="token-topup-hint">
                    {subscriptionInfo
                      ? 'Токены добавляются к текущему остатку'
                      : 'Доступно только при активной подписке. Оформите подписку выше.'}
                  </p>
                  <div className="one-time-grid">
                    {topupTariffs.map((tariff) => (
                      <div key={tariff.id} className={`one-time-card ${!subscriptionInfo ? 'disabled' : ''}`}>
                        <div className="ot-title">
                          <Zap size={20} fill="currentColor" />
                          {formatNumber(tariff.bonuses_amount)} токенов
                        </div>
                        <div className="ot-price">{formatNumber(tariff.price_rubles)} ₽</div>
                        {tariff.description && (
                          <div className="ot-rate">{tariff.description}</div>
                        )}
                        <button
                          type="button"
                          className="ot-btn"
                          disabled={!subscriptionInfo || (isProcessing && processingTariffId === tariff.id)}
                          onClick={() => handleSubscribe(tariff)}
                        >
                          {isProcessing && processingTariffId === tariff.id ? (
                            <><RefreshCw size={16} className="spinning" /> Оплата...</>
                          ) : (
                            <><CreditCard size={16} /> Купить</>
                          )}
                        </button>
                      </div>
                    ))}
                  </div>
                </div>
              )}

              {/* Блок отмены */}
              <div className="cancellation-notice">
                <ShieldCheck size={28} color="#2ecc71" className="flex-shrink-0 mt-1" />
                <div className="cancellation-text">
                  <strong>Безопасная подписка (Закон №376-ФЗ)</strong>
                  Сумма и периодичность списания указаны для каждого тарифа. Никаких скрытых списаний. Вы можете отменить подписку и автопродление в любой момент. После отмены подписка действует до конца оплаченного периода. Вторичные списания с удалённых карт исключены.
                  <br />По вопросам отмены подписки и возврата средств: <a href="https://t.me/selfai_support" style={{ color: '#3081ed' }}>https://t.me/selfai_support</a>
                </div>
              </div>
            </>
          ) : (
            <div className="legal-entity-content">
              <div className="legal-entity-header">
                <h2>Решения для бизнеса</h2>
                <p>Безналичный расчет, договоры, закрывающие документы и единый баланс.</p>
              </div>

              <div className="legal-features-grid">
                <div className="legal-feature">
                  <div className="feature-icon-wrapper"><Building2 size={24} /></div>
                  <h3>Документооборот</h3>
                  <p>Предоставляем оригиналы актов или работаем через ЭДО</p>
                </div>
                <div className="legal-feature">
                  <div className="feature-icon-wrapper"><Star size={24} /></div>
                  <h3>Оплата по счету</h3>
                  <p>Формируем счет-оферту без лишней бюрократии</p>
                </div>
                <div className="legal-feature">
                  <div className="feature-icon-wrapper"><User size={24} /></div>
                  <h3>Для всей команды</h3>
                  <p>Покупайте единый пакет токенов на всех сотрудников</p>
                </div>
              </div>

              <div className="contact-form-card">
                {contactSuccess ? (
                  <div className="contact-success">
                    <CheckCircle size={48} color="#2ecc71" style={{ marginBottom: '1rem' }} />
                    <h3>Ваша заявка принята!</h3>
                    <p style={{ color: '#a0a0a0', marginBottom: '2rem' }}>Наш менеджер свяжется с вами в ближайшее время.</p>
                    <button onClick={() => setContactSuccess(false)} className="ot-btn" style={{ padding: '0.8rem 2rem', fontSize: '1rem', width: 'auto' }}>
                      Отправить новую заявку
                    </button>
                  </div>
                ) : (
                  <form className="contact-form" onSubmit={handleContactSubmit}>
                    <h3>Свяжитесь с нами</h3>
                    <span className="form-note">Оставьте контактные данные для подключения, отмены подписки или запроса на возврат средств.</span>
                    <div className="form-group">
                      <label>Название компании</label>
                      <input type="text" name="companyName" value={contactData.companyName} onChange={handleContactChange} required placeholder="Например, ООО «Полет»" />
                    </div>
                    <div className="form-row">
                      <div className="form-group">
                        <label>Ваше имя</label>
                        <input type="text" name="contactName" value={contactData.contactName} onChange={handleContactChange} required />
                      </div>
                      <div className="form-group">
                        <label>Телефон</label>
                        <input type="tel" name="phone" value={contactData.phone} onChange={handleContactChange} />
                      </div>
                    </div>
                    <div className="form-group">
                      <label>Email</label>
                      <input type="email" name="email" value={contactData.email} onChange={handleContactChange} required />
                    </div>
                    <div className="form-group">
                      <label>Ваш вопрос</label>
                      <textarea name="message" value={contactData.message} onChange={handleContactChange} rows={3} placeholder="Опишите, какие объемы использования вас интересуют..." />
                    </div>
                    <button type="submit" className="submit-btn b2b" disabled={contactSubmitting}>
                      {contactSubmitting ? 'Отправляем...' : 'Оставить заявку'}
                    </button>
                  </form>
                )}
              </div>
            </div>
          )}
        </div>
      </div>
    </div>
  );
}

export default BonusTopup;
